/* global globalThis */
import useFieldValidation, { isBlank } from '../../components/useFieldValidation';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Boxes, Check, ChevronDown, Layers, Package, Plus, Scale, Trash2, Weight, Pencil } from 'lucide-react';
import { createPortal } from 'react-dom';
import CustomerModal from '../../components/CustomerModal';
import CompactBreadcrumb from '../../components/CompactBreadcrumb';
import { createCustomer } from '../customers/customerService';
import CustomerInventoryTable from '../customers/CustomerInventoryTable';
import { calculatePack } from './inventoryMeasurements';
import InventoryInlineRows from './InventoryInlineRows';
import TableScroll from '../../components/TableScroll';
import '../customers/customer-details.css';

function createReferenceId() {
  if (typeof globalThis.crypto.randomUUID === 'function') return globalThis.crypto.randomUUID();
  // getRandomValues is also available on LAN pages served over HTTP.
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function emptyProduct(lookups, batch) {
  return {
    product_id: '',
    name: '',
    unit_id: batch?.unit_id || lookups.units[0]?.id || '',
    quantity: '',
    cbm: '',
    net_weight: '',
    gross_weight: '',
  };
}

function emptyBatch(lookups) {
  return {
    priority_id: lookups.priorities[0]?.id || '',
    batch_number: '',
    batch_quantity: '1',
    unit_id: lookups.units[0]?.id || '',
    cbm: '',
    net_weight: '',
    gross_weight: '',
    products: [emptyProduct(lookups)],
  };
}

function emptyPackaging(lookups) {
  return {
    measurement_level: lookups.measurement_level || 'Carton',
    use_master_measurements: false,
    packaging_type_id: lookups.packaging_types[0]?.id || '',
    packaging_quantity: '1',
    total_carton_qty: '',
    cbm: '', net_weight: '', gross_weight: '',
    batches: [emptyBatch(lookups)],
  };
}

function emptyForm(lookups) {
  return {
    measurement_level: lookups.measurement_level || "Carton",
    warehouse_id: '',
    customer_id: '',
    priority_id: lookups.priorities[0]?.id || '',
    status: 'Available',
    marko: '',
    reference_number: '',
    inventory_date: today(),
    packaging: [emptyPackaging(lookups)],
  };
}

function toFormNumber(value) {
  if (value == null || value === '') return '';
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : '';
}

export function formFromInventory(item, lookups) {
  if (!item) return emptyForm(lookups);
  const toBatch = batch => ({
    priority_id: batch.priority_id || '',
    batch_number: batch.batch_number || '',
    batch_quantity: toFormNumber(batch.batch_quantity) || '1',
    unit_id: batch.unit_id || lookups.units[0]?.id || '',
    cbm: toFormNumber(batch.rawMeasurements?.cbm ?? batch.cbm),
    net_weight: toFormNumber(batch.rawMeasurements?.net_weight ?? batch.net_weight),
    gross_weight: toFormNumber(batch.rawMeasurements?.gross_weight ?? batch.gross_weight),
    products: batch.products?.length
      ? batch.products.map(product => ({
        inventory_batch_product_id: product.id || product.inventory_batch_product_id || undefined,
        product_id: product.product_id || '',
        name: product.name || '',
        unit_id: product.unit_id || batch.unit_id || lookups.units[0]?.id || '',
        quantity: toFormNumber(product.quantity),
        cbm: toFormNumber(product.cbm),
        net_weight: toFormNumber(product.net_weight),
        gross_weight: toFormNumber(product.gross_weight),
      }))
      : [emptyProduct(lookups)],
  });
  return {
    measurement_level: item.measurement_level || 'Carton',
    warehouse_id: item.warehouse_id || '',
    customer_id: item.customer_id || '',
    priority_id: item.priority_id || lookups.priorities[0]?.id || '',
    status: item.status || 'Available',
    marko: item.marko || '',
    reference_number: item.reference_number || '',
    inventory_date: item.inventory_date || today(),
    packaging: item.packaging?.length ? item.packaging.map(pack => ({
      measurement_level: pack.measurement_level || item.measurement_level || 'Carton',
      use_master_measurements: pack.use_master_measurements === true,
      cbm: toFormNumber(pack.rawMeasurements?.cbm),
      net_weight: toFormNumber(pack.rawMeasurements?.net_weight),
      gross_weight: toFormNumber(pack.rawMeasurements?.gross_weight),
      packaging_type_id: pack.packaging_type_id || lookups.packaging_types[0]?.id || '',
      packaging_quantity: toFormNumber(pack.packaging_quantity) || '1',
      total_carton_qty: toFormNumber(pack.total_carton_qty),
      batches: pack.batches?.length ? pack.batches.map(toBatch) : [emptyBatch(lookups)],
    })) : [{
      packaging_type_id: item.packaging_type_id || lookups.packaging_types[0]?.id || '',
      packaging_quantity: toFormNumber(item.packaging_quantity) || '1',
      total_carton_qty: toFormNumber(item.total_carton_qty),
      batches: [toBatch(item)],
    }],
  };
}

function packagingKind(name) {
  const key = String(name || '').toLowerCase();
  if (key.includes('pallet')) return 'pallet';
  if (key.includes('carton')) return 'carton';
  return 'other';
}

export function packagingQuantities(pack, lookups = {}) {
  const type = lookups.packaging_types?.find(row => String(row.id) === String(pack.packaging_type_id));
  const kind = packagingKind(type?.name || pack.packaging_type);
  const cartons = (pack.batches || []).reduce((sum, batch) => sum + asNumber(batch.batch_quantity), 0);
  return {
    packaging_quantity: kind === 'carton' ? cartons : asNumber(pack.packaging_quantity),
    total_carton_qty: cartons,
  };
}

function packagingCopy(name, index) {
  const kind = packagingKind(name);
  if (kind === 'pallet') {
    return {
      title: `Pallet #${index + 1} Consignment Intake`,
      badge: 'Palletized Unit',
      remove: 'Remove Pallet',
      qty: 'Pallet Qty',
      cartons: 'Total Cartons on Pallet',
    };
  }
  if (kind === 'carton') {
    return {
      title: `Consignment Intake Record #${index + 1}`,
      badge: 'Individual Cartons',
      remove: 'Remove Consignment',
      qty: 'Total Packaging Qty',
      cartons: 'Total Cartons in Entry',
    };
  }
  return {
    title: `Consignment Intake Record #${index + 1}`,
    badge: name || 'Consignment',
    remove: 'Remove Consignment',
    qty: 'Total Packaging Qty',
    cartons: 'Total Cartons in Entry',
  };
}

function priorityTone(name) {
  const key = String(name || '').toLowerCase();
  if (key.includes('high')) return 'high';
  if (key.includes('low')) return 'low';
  return 'medium';
}

function asNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function isFilledProduct(product) {
  return Boolean(String(product?.name || '').trim() || product?.product_id || product?.quantity);
}

export function buildInventorySummary(form, lookups = {}) {
  let packages = 0;
  let cartons = 0;
  let products = 0;
  let volume = 0;
  let net = 0;
  let gross = 0;
  (form?.packaging || []).forEach(pack => {
    const level = pack.measurement_level || form.measurement_level || 'Carton';
    const quantities = packagingQuantities(pack, lookups);
    packages += quantities.packaging_quantity;
    cartons += quantities.total_carton_qty;
    if (level === 'Pallet' || pack.use_master_measurements) {
      for (const field of ['cbm', 'net_weight', 'gross_weight']) if (asNumber(pack[field]) > 0) {
        if (field === 'cbm') volume += asNumber(pack[field]);
        if (field === 'net_weight') net += asNumber(pack[field]);
        if (field === 'gross_weight') gross += asNumber(pack[field]);
      }
    }
    (pack.batches || []).forEach(batch => {
      if (level === 'Carton') {
        if (!pack.use_master_measurements || !(asNumber(pack.cbm) > 0)) volume += asNumber(batch.cbm);
        if (!pack.use_master_measurements || !(asNumber(pack.net_weight) > 0)) net += asNumber(batch.net_weight);
        if (!pack.use_master_measurements || !(asNumber(pack.gross_weight) > 0)) gross += asNumber(batch.gross_weight);
      }
      (batch.products || []).forEach(product => {
        if (!isFilledProduct(product)) return;
        products += 1;
        if (level === 'Product') {
          if (!pack.use_master_measurements || !(asNumber(pack.cbm) > 0)) volume += asNumber(product.cbm);
          if (!pack.use_master_measurements || !(asNumber(pack.net_weight) > 0)) net += asNumber(product.net_weight);
          if (!pack.use_master_measurements || !(asNumber(pack.gross_weight) > 0)) gross += asNumber(product.gross_weight);
        }
      });
    });
  });
  return { packages, cartons, products, volume, net, gross };
}

function formatSummary(value, digits = 0) {
  const number = asNumber(value);
  return number.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function batchTotalErrors(batch, fields = ['cbm', 'net_weight', 'gross_weight']) {
  return [['cbm', 'CBM'], ['net_weight', 'net weight'], ['gross_weight', 'gross weight']].filter(([field]) => fields.includes(field))
    .flatMap(([field, label]) => {
      const total = batch.products.reduce((sum, product) => sum + asNumber(product[field]), 0);
      const limit = asNumber(batch[field]);
      const tolerance = Number.EPSILON * Math.max(1, Math.abs(total), Math.abs(limit)) * (batch.products.length + 1);
      return total - limit > tolerance
        ? [`Product ${label} total (${total}) not greater than carton ${label} (${limit}).`]
        : [];
    });
}

function measurementLabel(field) {
  return { cbm: 'CBM', net_weight: 'Net wt', gross_weight: 'Gross Wt' }[field];
}

export function validateForm(form, interactive = false, lookups = {}) {
  const errors = {};
  if (isBlank(form.warehouse_id)) errors.warehouse_id = 'Warehouse is required.';
  if (isBlank(form.customer_id)) errors.customer_id = 'Customer is required.';
  if (isBlank(form.priority_id)) errors.priority_id = 'Priority is required.';
  if (isBlank(form.inventory_date)) errors.inventory_date = 'Inventory date is required.';
  let productCount = 0;
  form.packaging.forEach((pack, packIndex) => {
    const level = pack.measurement_level || form.measurement_level || 'Carton';
    if (level === 'Pallet' && !pack.use_master_measurements) for (const field of ['cbm', 'net_weight', 'gross_weight']) {
      const label = measurementLabel(field);
      if (isBlank(pack[field])) errors[`pack_${field}_${packIndex}`] = `${label} is required.`;
      else if (!Number.isFinite(Number(pack[field])) || !(Number(pack[field]) > 0)) errors[`pack_${field}_${packIndex}`] = `${label} must be greater than 0.`;
    }
    if (isBlank(pack.packaging_type_id)) errors[`pack_type_${packIndex}`] = 'Type is required.';
    if (isBlank(packagingQuantities(pack, lookups).packaging_quantity)) errors[`pack_qty_${packIndex}`] = 'Qty is required.';
    else if (!(packagingQuantities(pack, lookups).packaging_quantity > 0)) errors[`pack_qty_${packIndex}`] = 'Qty must be greater than 0.';
    if (!pack.batches.length) errors[`cartons_${packIndex}`] = 'Add at least one carton in a consignment.';
    pack.batches.forEach((batch, batchIndex) => {
      if (!(Number(batch.unit_id) > 0)) errors[`unit_${packIndex}_${batchIndex}`] = 'Unit is required.';
      if (isBlank(batch.batch_quantity)) errors[`batch_qty_${packIndex}_${batchIndex}`] = 'Carton quantity is required.';
      else if (!(Number(batch.batch_quantity) > 0)) errors[`batch_qty_${packIndex}_${batchIndex}`] = 'Carton quantity must be greater than 0.';
      for (const field of ['cbm', 'net_weight', 'gross_weight']) {
        if (level !== 'Carton' || pack.use_master_measurements && Number(pack[field]) > 0) continue;
        const key = `batch_${field}_${packIndex}_${batchIndex}`;
        const errorLabel = measurementLabel(field);
        if (isBlank(batch[field])) errors[key] = `${errorLabel} is required.`;
        else if (!Number.isFinite(Number(batch[field])) || Number(batch[field]) <= 0) errors[key] = `${errorLabel} must be greater than 0.`;
      }
      const products = batch.products.filter(product => product.name.trim() || product.product_id || product.quantity);
      const totalErrors = level === 'Carton' ? batchTotalErrors(batch, ['cbm', 'net_weight', 'gross_weight'].filter(field => !(pack.use_master_measurements && Number(pack[field]) > 0))) : [];
      if (totalErrors.length) errors[`products_${packIndex}_${batchIndex}`] = totalErrors.join(' ');
      if (!products.length) errors[`products_${packIndex}_${batchIndex}`] = 'Add at least one product.';
      batch.products.forEach((product, productIndex) => {
        const filled = product.name.trim() || product.product_id || product.quantity;
        if (level === 'Product' && (interactive || filled)) for (const field of ['cbm', 'net_weight', 'gross_weight'].filter(field => !(pack.use_master_measurements && Number(pack[field]) > 0))) {
          const label = measurementLabel(field);
          const key = `product_${field}_${packIndex}_${batchIndex}_${productIndex}`;
          if (isBlank(product[field])) errors[key] = 'Required';
          else if (!Number.isFinite(Number(product[field])) || !(Number(product[field]) > 0)) errors[key] = `${label} must be greater than 0.`;
        }
        if (!filled) {
          // Empty extra rows remain optional at submission, as before.
          if (!interactive) return;
          errors[`product_${packIndex}_${batchIndex}_${productIndex}`] = 'Required';
          errors[`product_qty_${packIndex}_${batchIndex}_${productIndex}`] = 'Required';
          return;
        }
        productCount += 1;
        if (!product.product_id && !product.name.trim()) errors[`product_${packIndex}_${batchIndex}_${productIndex}`] = 'Required';
        if (isBlank(product.quantity)) errors[`product_qty_${packIndex}_${batchIndex}_${productIndex}`] = 'Required';
        else if (!(Number(product.quantity) > 0)) errors[`product_qty_${packIndex}_${batchIndex}_${productIndex}`] = 'Prod Qty must be greater than 0.';
      });
    });
  });
  if (!productCount) errors.products = 'Add at least one product.';
  return errors;
}

function selectOptionText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (!React.isValidElement(node)) return '';
  return React.Children.toArray(node.props.children).map(selectOptionText).join(' ');
}

export function FieldSelect({ open, onToggle, onClose, onValidate, onSearchChange, value, placeholder, error, compact, dropUp, children, ariaLabel, disabled = false, floating = false, floatingMinWidth = 220, searchLabel, showOptionsAfterSearch = false, autoFocus = false }) {
  const ref = useRef(null);
  const searchRef = useRef(null);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  const openedOnFocus = useRef(false);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [showAllOptions, setShowAllOptions] = useState(false);
  useEffect(() => { if (!open) setShowAllOptions(false); }, [open]);
  const optionsToShow = searchLabel ? React.Children.toArray(children).filter(child =>
    selectOptionText(child).toLowerCase().includes(query.trim().toLowerCase())) : children;
  const showMenu = open && (showAllOptions || !showOptionsAfterSearch || query.trim().length > 0);
  const [menuPosition, setMenuPosition] = useState(null);
  useEffect(() => {
    if (!open || !floating) return undefined;
    function position() {
      const bounds = ref.current?.querySelector('.wh-form-select-trigger')?.getBoundingClientRect();
      if (!bounds) return;
      const width = Math.min(Math.max(bounds.width, floatingMinWidth), window.innerWidth - 16);
      const height = Math.min(160, window.innerHeight * .35);
      const below = window.innerHeight - bounds.bottom - 8;
      setMenuPosition({ position: 'fixed', left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
        top: below >= height ? bounds.bottom + 4 : Math.max(8, bounds.top - height - 4),
        bottom: 'auto', width, minWidth: 0, zIndex: 1000 });
    }
    function handleScroll(event) {
      if (!ref.current?.querySelector('[role="listbox"]')?.contains(event.target)) position();
    }
    position();
    window.addEventListener('resize', position);
    document.addEventListener('scroll', handleScroll, true);
    return () => { window.removeEventListener('resize', position); document.removeEventListener('scroll', handleScroll, true); };
  }, [open, floating, floatingMinWidth]);
  useEffect(() => {
    if (open) {
      if (searchLabel) { searchRef.current?.focus({ preventScroll: true }); return; }
      const search = ref.current?.querySelector('input[role="combobox"]');
      if (search) { search.focus(); return; }
      const menu = ref.current?.querySelector('[role="listbox"]');
      (menu?.querySelector('.selected') || menu?.querySelector('[role="option"]'))?.focus();
    }
  }, [open, searchLabel]);
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event) {
      if (!ref.current?.contains(event.target)) { onValidate?.(); onClose(); }
    }
    function handleKey(event) {
      if (event.key === 'Escape') { openedOnFocus.current = false; onValidate?.(); onClose(); }
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, onValidate]);

  return <div className={`wh-form-select${error ? ' invalid' : ''}${compact ? ' rg-select' : ''}${dropUp ? ' rg-select-up' : ''}${open ? ' open' : ''}`} ref={ref} onKeyDown={event => {
    if (event.target.matches('input[role="combobox"]') && ['Home', 'End'].includes(event.key)) return;
    if (!open && ['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Escape'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Escape') {
      openedOnFocus.current = false;
      event.stopPropagation();
      onClose();
      ref.current?.querySelector('.wh-form-select-trigger')?.focus();
      return;
    }
    if (!open) { onToggle(); return; }
    const options = Array.from(ref.current.querySelectorAll('[role="option"]:not(:disabled)'));
    if (!options.length) return;
    const current = options.indexOf(document.activeElement);
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : current < 0 ? (event.key === 'ArrowUp' ? options.length - 1 : 0) :
      (current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    options[index].focus();
    options[index].scrollIntoView?.({ block: 'nearest' });
  }} onClick={event => {
    if (event.target.closest('[role="option"]')) {
      setQuery('');
      onSearchChangeRef.current?.('');
      ref.current?.querySelector('.wh-form-select-trigger')?.focus({ preventScroll: true });
    }
  }} onBlur={event => { if (!ref.current?.contains(event.relatedTarget)) { openedOnFocus.current = false; onValidate?.(); if (open) onClose(); } }}>
    {searchLabel ? <input
      ref={searchRef}
      className={`wh-form-select-trigger${open ? ' open' : ''}${value ? '' : ' placeholder'}`}
      type="text"
      role="combobox"
      aria-label={searchLabel}
      aria-controls={listId}
      aria-autocomplete="list"
      autoComplete="off"
      autoFocus={autoFocus}
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-invalid={Boolean(error)}
      value={open ? query : selectOptionText(value) || query || placeholder || ''}
      placeholder={open ? searchLabel : placeholder}
      onKeyDown={event => {
        if (event.key === 'Escape') { openedOnFocus.current = false; event.stopPropagation(); onValidate?.(); onClose(); searchRef.current?.blur(); }
      }}
      onFocus={event => { if (!open && !disabled && !ref.current?.contains(event.relatedTarget)) { openedOnFocus.current = true; onToggle(); } }}
      onClick={() => { if (openedOnFocus.current) { openedOnFocus.current = false; return; } if (!open) onToggle(); }}
      onChange={event => { setQuery(event.target.value); onSearchChange?.(event.target.value); if (!open) onToggle(); }}
    /> : <button
      className={`wh-form-select-trigger${open ? ' open' : ''}${value ? '' : ' placeholder'}`}
      type="button"
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-invalid={Boolean(error)}
      onFocus={event => { if (!open && !disabled && !ref.current?.contains(event.relatedTarget)) onToggle(); }}
      onClick={() => { if (!open) onToggle(); }}
    ><span className="wh-form-select-value">{value || placeholder}</span></button>}
    <button type="button" tabIndex={-1} className="rg-select-arrow" aria-label={`Show all ${searchLabel?.replace(/^Search /i, '') || ariaLabel || placeholder || 'options'}`}
      disabled={disabled} aria-haspopup="listbox" aria-expanded={showMenu} aria-controls={listId}
      onMouseDown={event => event.preventDefault()}
      onClick={() => {
        setQuery('');
        onSearchChangeRef.current?.('');
        setShowAllOptions(true);
        if (!open) onToggle();
        searchRef.current?.focus({ preventScroll: true });
      }}>
      <ChevronDown size={14} strokeWidth={1.8} />
    </button>
    {showMenu && <div className={`wh-form-select-menu${dropUp ? ' drop-up' : ''}`} style={floating ? menuPosition : undefined}>
      <div id={listId} role="listbox">{optionsToShow}{searchLabel && !optionsToShow.length && <p className="rg-dropdown-empty">No results found.</p>}</div>
    </div>}
  </div>;
}

export default function InventoryModal({ mode, item, lookups, onClose, onSubmit, onToast, onEdit, onBackToDetails, inline = false, canCreateCustomer = false }) {
  const readOnly = mode === 'view';
  const isEdit = mode === 'edit' || readOnly;
  const [form, setForm] = useState(() => formFromInventory(item, lookups));
  const { errors, setErrors, touchField } = useFieldValidation(form, (values, interactive) => validateForm(values, interactive, lookups));
  const [saving, setSaving] = useState(false);
  const [customerToAdd, setCustomerToAdd] = useState(null);
  const [customerQuery, setCustomerQuery] = useState('');
  const [createdCustomers, setCreatedCustomers] = useState([]);
  const [productMeasurements, setProductMeasurements] = useState({});
  const formElement = useRef(null);
  const [pendingProductFocus, setPendingProductFocus] = useState(null);
  useEffect(() => {
    if (!pendingProductFocus) return;
    const field = formElement.current?.querySelector(`[aria-label="${pendingProductFocus}"]`)
      || (pendingProductFocus.startsWith('Packaging Type ') && Array.from(formElement.current?.querySelectorAll('[aria-label^="Packaging Type "]') || []).at(-1));
    if (field) { field.focus(); setPendingProductFocus(null); }
  }, [pendingProductFocus]);
  const [cartonToDelete, setCartonToDelete] = useState(null);
  const [inlineEditing, setInlineEditing] = useState(false);
  const inlineEditor = useRef(null);
  const [initialCell, setInitialCell] = useState(null);
  const [openSelect, setOpenSelect] = useState(readOnly ? null : 'warehouse');
  const [newReference] = useState(() => isEdit ? '' : `REC-${createReferenceId()}`);
  const titleId = 'inv-form-title';

  const selectedWarehouse = lookups.warehouses.find(row => String(row.id) === String(form.warehouse_id));
  const customerOptions = [...lookups.customers, ...createdCustomers];
  const selectedCustomer = customerOptions.find(row => String(row.id) === String(form.customer_id));
  const customerNameToCreate = customerQuery.trim();
  const customerError = customerNameToCreate && errors.customer_id === 'Customer is required.' ? undefined : errors.customer_id;
  const selectedPriority = lookups.priorities.find(row => String(row.id) === String(form.priority_id));

  useEffect(() => {
    const initial = formFromInventory(item, lookups);
    if (!isEdit && initial.measurement_level === 'Pallet' && initial.packaging.every(pack =>
      packagingKind(lookups.packaging_types.find(type => String(type.id) === String(pack.packaging_type_id))?.name) === 'carton')) {
      initial.measurement_level = 'Carton';
      initial.packaging = initial.packaging.map(pack => ({ ...pack, measurement_level: 'Carton' }));
    }
    setForm({ ...initial, reference_number: isEdit ? item?.reference_number || '' : newReference });
    setErrors({});
    setOpenSelect(readOnly ? null : 'warehouse');
  }, [mode, item, lookups, isEdit, newReference, setErrors, readOnly]);

  useEffect(() => {
    function handleKey(event) {
      if (event.key === 'Escape' && !event.defaultPrevented && !saving && !openSelect) onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, openSelect, saving]);

  function updateField(name, value) {
    setForm(current => ({ ...current, [name]: value }));
    setErrors(current => ({ ...current, [name]: undefined }));
  }

  function updatePackaging(packIndex, name, value) {
    if (['packaging_quantity', 'total_carton_qty', 'cbm', 'net_weight', 'gross_weight'].includes(name) && !/^\d*\.?\d*$/.test(value)) return;
    setForm(current => {
      const cartonSelected = !isEdit && name === 'packaging_type_id' && packagingKind(lookups.packaging_types.find(type => String(type.id) === String(value))?.name) === 'carton';
      return {
        ...current,
        packaging: current.packaging.map((pack, index) => {
          if (index !== packIndex) return pack;
          if (cartonSelected && pack.measurement_level === 'Pallet') {
            return { ...calculatePack(pack, 'Pallet'), [name]: value, measurement_level: 'Carton' };
          }
          return { ...pack, [name]: value };
        }),
      };
    });
    setErrors(current => ({ ...current, [`pack_type_${packIndex}`]: undefined, [`pack_qty_${packIndex}`]: undefined, [`pack_${name}_${packIndex}`]: undefined }));
  }

  function updatePackagingMeasurement(packIndex, level) {
    setForm(current => ({
      ...current,
      measurement_level: packIndex === 0 ? level : current.measurement_level,
      packaging: current.packaging.map((pack, index) => index === packIndex ? { ...pack, measurement_level: level } : pack),
    }));
    setErrors({});
  }

  function updateBatch(packIndex, batchIndex, name, value) {
    if (['batch_quantity', 'cbm', 'net_weight', 'gross_weight'].includes(name) && !/^\d*\.?\d*$/.test(value)) return;
    setForm(current => ({
      ...current,
      packaging: current.packaging.map((pack, index) => (
        index === packIndex
          ? { ...pack, batches: pack.batches.map((batch, inner) => (inner === batchIndex ? { ...batch, [name]: value } : batch)) }
          : pack
      )),
    }));
    setErrors(current => ({ ...current, [`unit_${packIndex}_${batchIndex}`]: undefined, [`batch_qty_${packIndex}_${batchIndex}`]: undefined, [`batch_priority_${packIndex}_${batchIndex}`]: undefined, [`batch_${name}_${packIndex}_${batchIndex}`]: undefined }));
  }

  function updateProduct(packIndex, batchIndex, productIndex, name, value) {
    if (['quantity', 'cbm', 'net_weight', 'gross_weight'].includes(name) && !/^\d*\.?\d*$/.test(value)) return;
    let limitMessage;
    if (!form.packaging[packIndex]?.use_master_measurements && (form.packaging[packIndex]?.measurement_level || form.measurement_level) === 'Carton' && ['net_weight', 'cbm', 'gross_weight'].includes(name) && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0) {
      const batch = form.packaging[packIndex].batches[batchIndex];
      const candidate = { ...batch, products: batch.products.map((product, index) => index === productIndex ? { ...product, [name]: value } : product) };
      if (batchTotalErrors(candidate, [name]).length) {
        value = '0';
        const label = { net_weight: 'Net weight', cbm: 'CBM', gross_weight: 'Gross weight' }[name];
        const limit = name === 'cbm' ? `${asNumber(batch[name]).toFixed(2)} m³` : `${asNumber(batch[name])} kg`;
        limitMessage = `${label} is greater than the carton limit of ${limit}. Value reset to 0.`;
      }
    }
    setForm(current => ({
      ...current,
      packaging: current.packaging.map((pack, index) => (
        index === packIndex
          ? {
            ...pack,
            batches: pack.batches.map((batch, inner) => (
              inner === batchIndex
                ? { ...batch, unit_id: name === 'unit_id' && !batch.unit_id ? value : batch.unit_id, products: batch.products.map((product, productInner) => (productInner === productIndex ? { ...product, [name]: value } : product)) }
                : batch
            )),
          }
          : pack
      )),
    }));
    setErrors(current => ({
      ...current,
      [`product_limit_${name}_${packIndex}_${batchIndex}_${productIndex}`]: limitMessage,
      products: undefined,
      [`products_${packIndex}_${batchIndex}`]: undefined,
      [`product_${packIndex}_${batchIndex}_${productIndex}`]: undefined,
      [`product_qty_${packIndex}_${batchIndex}_${productIndex}`]: undefined,
    }));
  }

  function closeSelect() {
    setOpenSelect(null);
  }

  function openAddCustomer() {
    if (!canCreateCustomer || !customerNameToCreate) return;
    closeSelect();
    setCustomerToAdd({ name: customerNameToCreate });
  }

  async function saveNewCustomer(values) {
    if (!canCreateCustomer) throw new Error('You do not have permission to add customers.');
    const customer = await createCustomer(values);
    setCreatedCustomers(current => [...current, customer]);
    updateField('customer_id', customer.id);
    setCustomerQuery('');
    onToast?.(`Customer "${customer.name}" added.`, 'success');
  }

  const summary = useMemo(() => buildInventorySummary(form, lookups), [form, lookups]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (readOnly) return;
    if (saving) return;
    setSaving(true);
    try {
      let submissionForm = form;
      if (isBlank(submissionForm.customer_id) && customerNameToCreate && canCreateCustomer) {
        openAddCustomer();
        return;
      }
      const nextErrors = validateForm(submissionForm, false, lookups);
      if (Object.keys(nextErrors).length) {
        setErrors(nextErrors);
        return;
      }
      const payload = inventoryPayload(submissionForm, lookups, isEdit ? submissionForm.reference_number : newReference);
      await onSubmit(payload);
      onClose();
    } catch (error) {
      setErrors({ form: error.message });
    } finally {
      setSaving(false);
    }
  }

  const title = readOnly ? 'Inventory Details' : isEdit ? 'Edit Inventory' : 'Add Inventory';

  if (readOnly) {
    const inventory = { ...form, id: item?.id, packaging: form.packaging.map(pack => calculatePack(pack, pack.measurement_level || form.measurement_level)).map((pack, packIndex) => ({
      ...pack, id: pack.id || `pack-${packIndex}`,
      packaging_type: lookups.packaging_types.find(type => String(type.id) === String(pack.packaging_type_id))?.name || pack.packaging_type || 'Packaging',
      batches: pack.batches.map((batch, batchIndex) => ({ ...batch, id: batch.id || `batch-${batchIndex}`,
        products: batch.products.filter(product => product.name).map((product, productIndex) => ({ ...product, id: product.id || `product-${productIndex}` })),
      })),
    })) };
    const cards = [[Boxes, 'blue', 'Total Packages', summary.packages, 'Units'], [Package, 'amber', 'Total Cartons', summary.cartons, 'Cartons'],
      [Layers, 'violet', 'Total Products', summary.products, 'SKUs'], [Box, 'orange', 'Total Volume', summary.volume, 'CBM'],
      [Scale, 'cyan', 'Total Net Weight', summary.net, 'kg'], [Weight, 'pink', 'Total Gross Weight', summary.gross, 'kg']];
    return <section className="wh-page wh-list-page inv-page customer-details-page inventory-details-page">
      <header className="wh-title"><div><CompactBreadcrumb parent="Inventory" current={title} onNavigate={onClose} /><h1>{title}</h1></div>
        <div className="inventory-details-actions">
        {!inlineEditing && onEdit && <button type="button" className="wh-add" onClick={onEdit}><Pencil size={14} />Edit Inventory</button>}
        </div>
      </header>
      <div className="wh-kpis inventory-details-kpis" aria-label="Inventory Summary">{cards.map(([Icon, tone, label, value, unit]) => <article className="wh-kpi" key={label}>
        <span className={`wh-kpi-icon ${tone}`}><Icon size={18} /></span><div className="wh-kpi-copy"><span>{label}</span><strong>{formatSummary(value)} <small>{unit}</small></strong></div>
      </article>)}</div>
      <div className="wh-panel inventory-details-intake"><h2>Intake Information</h2><dl>
        {[[ 'Warehouse', selectedWarehouse?.name || item?.warehouse_name ], ['Customer', selectedCustomer?.name || item?.customer_name],
          ['Priority', selectedPriority?.name], ['Marko', form.marko], ['Receiving Date', form.inventory_date]].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || '?'}</dd></div>)}
      </dl></div>
      <div className="wh-panel"><div className="customer-cargo-heading inventory-cargo-heading"><h2>Associated Goods &amp; Stored Inventory</h2></div>
        {inlineEditing ? <TableScroll><table className="wh-table inv-table"><thead><tr>{['Actions', 'Type', 'Qty', 'Carton Qty', 'Product', 'Prod Qty', 'Prod Gross (kg)', 'Prod Net (kg)', 'Carton Gross (kg)', 'Carton Net (kg)', 'Carton CBM', 'Gross Wt (kg)', 'Net Wt (kg)', 'Total CBM', 'Marko'].map(label => <th key={label}>{label.replace(' (kg)', '').replace(/^Product (?=Qty|Gross|Net)/, 'Prod ')}</th>)}</tr></thead><tbody>
          <InventoryInlineRows locationColumnsLast showCartonMeasurements hideLocationColumns ref={inlineEditor} initialCell={initialCell} item={item} lookups={lookups} onSave={onSubmit} onCancel={() => setInlineEditing(false)} onToast={onToast} />
        </tbody></table></TableScroll> : <CustomerInventoryTable markoLast hideWeightUnits showCartonMeasurements onEditCell={onEdit ? label => { setInitialCell(label); setInlineEditing(true); } : undefined} inventory={[inventory]} summary={{ totalProducts: summary.products, totalQuantity: form.packaging.reduce((total, pack) => total + pack.batches.reduce((sum, batch) => sum + batch.products.reduce((qty, product) => qty + Number(product.quantity || 0), 0), 0), 0), totalGrossWeight: summary.gross, totalNetWeight: summary.net }} />}
      </div>
    </section>;
  }

  function moveInputFocus(event) {
    if (!event.currentTarget.contains(event.target)) return;
    let input = event.target;
    if (input.matches('[role="option"]') && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      input = input.closest('.wh-form-select')?.querySelector('.wh-form-select-trigger') || input;
    }
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.nativeEvent.isComposing
      || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
      || !(input.matches('input[type="date"], input[type="text"], input[type="number"], input[type="search"], input:not([type]), button.wh-form-select-trigger'))
      || (input.closest('.wh-form-select.open') && ['ArrowUp', 'ArrowDown'].includes(event.key))) return;
    // Preserve text editing until the caret reaches the edge of the field.
    if (input.tagName === 'INPUT' && event.key === 'ArrowLeft' && input.selectionStart !== null && (input.selectionStart !== 0 || input.selectionEnd !== 0)) return;
    if (input.tagName === 'INPUT' && event.key === 'ArrowRight' && input.selectionEnd !== null && (input.selectionStart !== input.value.length || input.selectionEnd !== input.value.length)) return;
    const inputs = Array.from(event.currentTarget.querySelectorAll('input, button.wh-form-select-trigger')).filter(field =>
      (field.matches('button.wh-form-select-trigger') || ['text', 'number', 'date', 'search'].includes(field.type)) && !field.matches(':disabled') && !field.readOnly
      && !field.closest('[hidden], [aria-hidden="true"]'));
    const index = inputs.indexOf(input);
    if (index < 0) return;
    let next;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      next = inputs[index + (event.key === 'ArrowRight' ? 1 : -1)];
    } else {
      const source = input.getBoundingClientRect();
      const down = event.key === 'ArrowDown';
      const candidates = inputs.filter(field => field !== input).map(field => ({ field, rect: field.getBoundingClientRect() }))
        .filter(({ rect }) => down ? rect.top >= source.bottom : rect.bottom <= source.top)
        .filter(({ rect }) => rect.width > 0 && rect.height > 0);
      candidates.sort((a, b) => {
        const distance = rect => Math.abs((down ? rect.top - source.bottom : source.top - rect.bottom));
        return distance(a.rect) - distance(b.rect)
          || Math.abs(a.rect.left + a.rect.width / 2 - source.left - source.width / 2) - Math.abs(b.rect.left + b.rect.width / 2 - source.left - source.width / 2);
      });
      next = candidates[0]?.field;
    }
    if (next) { event.preventDefault(); next.focus(); }
  }

  return <form ref={formElement} className={`rg-page${inline ? ' rg-inline' : ''}${readOnly ? ' rg-view' : ''}${!readOnly && !isEdit ? ' rg-receive' : ''}`} role={readOnly || inline ? undefined : 'dialog'} aria-modal={readOnly || inline ? undefined : true} aria-labelledby={readOnly || isEdit ? titleId : undefined} aria-label={!readOnly && !isEdit ? title : undefined} onSubmit={event => event.preventDefault()} onKeyDown={moveInputFocus}>
    <div className="rg-title">
      <div>
        <CompactBreadcrumb parent="Inventory" current={title} onNavigate={onClose} disabled={saving} items={mode === 'edit' && onBackToDetails ? [
          { label: 'Inventory', onNavigate: onClose },
          { label: 'Inventory Details', onNavigate: onBackToDetails },
        ] : undefined} />
        {(readOnly || isEdit) && <h2 id={titleId}>{title}</h2>}
      </div>
      {readOnly && <div className="rg-view-actions">
        {!inlineEditing && onEdit && <button type="button" className="wh-add" onClick={onEdit}><Pencil size={14} />Edit</button>}
      </div>}
    </div>

      <section className="rg-summary" aria-label="Inventory Summary">
        <article className="rg-kpi">
          <span className="rg-kpi-icon blue"><Boxes size={16} /></span>
          <div>
            <span>Total Packages</span>
            <strong>{formatSummary(summary.packages)} <small>Units</small></strong>
          </div>
        </article>
        <article className="rg-kpi">
          <span className="rg-kpi-icon amber"><Package size={16} /></span>
          <div>
            <span>Total Cartons</span>
            <strong>{formatSummary(summary.cartons)} <small>Cartons</small></strong>
          </div>
        </article>
        <article className="rg-kpi">
          <span className="rg-kpi-icon violet"><Layers size={16} /></span>
          <div>
            <span>Total Products</span>
            <strong>{formatSummary(summary.products)} <small>SKUs</small></strong>
          </div>
        </article>
        <article className="rg-kpi">
          <span className="rg-kpi-icon orange"><Box size={16} /></span>
          <div>
            <span>Total Volume</span>
            <strong>{formatSummary(summary.volume, 3)} <small>CBM</small></strong>
          </div>
        </article>
        <article className="rg-kpi">
          <span className="rg-kpi-icon cyan"><Scale size={16} /></span>
          <div>
            <span>Total Net Weight</span>
            <strong>{formatSummary(summary.net)} <small>kg</small></strong>
          </div>
        </article>
        <article className="rg-kpi">
          <span className="rg-kpi-icon rose"><Weight size={16} /></span>
          <div>
            <span>Total Gross Weight</span>
            <strong>{formatSummary(summary.gross)} <small>kg</small></strong>
          </div>
        </article>
      </section>

    <div className="rg-scroll">

      <section className="rg-card" inert={readOnly ? true : undefined}>
        <h3>Intake Information</h3>

          <div className={`rg-intake${readOnly ? '' : ' rg-intake-no-reference'}`}>
            <div className="rg-field">
              <span>Warehouse <em>*</em></span>
              <FieldSelect autoFocus={!readOnly} compact searchLabel="Search warehouses" showOptionsAfterSearch open={openSelect === 'warehouse'} onToggle={() => setOpenSelect(openSelect === 'warehouse' ? null : 'warehouse')} onClose={closeSelect} onSearchChange={query => {
                if (!query.trim()) return;
                const matches = lookups.warehouses.filter(row => row.name.trim().toLowerCase() === query.trim().toLowerCase());
                updateField('warehouse_id', matches.length === 1 ? matches[0].id : '');
              }} value={selectedWarehouse?.name || ''} placeholder="Select warehouse" error={errors.warehouse_id} onValidate={() => touchField('warehouse_id')}>
                {lookups.warehouses.map(row => <button key={row.id} className={`wh-form-option${String(form.warehouse_id) === String(row.id) ? ' selected' : ''}`} type="button" role="option" onClick={() => { updateField('warehouse_id', row.id); closeSelect(); }}>{row.name}</button>)}
              </FieldSelect>
              {errors.warehouse_id && <small className="wh-form-error">{errors.warehouse_id}</small>}
            </div>
            <div className="rg-field">
              <span>Customer <em>*</em></span>
              <FieldSelect compact searchLabel="Search customers" showOptionsAfterSearch open={openSelect === 'customer'} onToggle={() => setOpenSelect(openSelect === 'customer' ? null : 'customer')} onClose={closeSelect} onSearchChange={setCustomerQuery} value={selectedCustomer?.name || ''} placeholder="Select customer" error={customerError} onValidate={() => touchField('customer_id')}>
                {customerOptions.map(row => <button key={row.id} className={`wh-form-option${String(form.customer_id) === String(row.id) ? ' selected' : ''}`} type="button" role="option" aria-selected={String(form.customer_id) === String(row.id)} onClick={() => { updateField('customer_id', row.id); setCustomerQuery(''); closeSelect(); }}>{row.name}</button>)}
                {canCreateCustomer && customerNameToCreate && !customerOptions.some(row => row.name.trim().toLowerCase() === customerNameToCreate.toLowerCase()) && <button className="wh-form-option" type="button" role="option" aria-selected={false} onClick={openAddCustomer}>+ Add Customer "{customerNameToCreate}"</button>}
              </FieldSelect>
              {customerError && <small className="wh-form-error">{customerError}</small>}
            </div>
            <div className="rg-field">
              <span>Priority <em>*</em></span>
              <FieldSelect compact open={openSelect === 'priority'} onToggle={() => setOpenSelect(openSelect === 'priority' ? null : 'priority')} onClose={closeSelect} value={selectedPriority ? <span className={`rg-priority ${priorityTone(selectedPriority.name)}`}>{selectedPriority.name}</span> : ''} placeholder="Select priority" error={errors.priority_id} onValidate={() => touchField('priority_id')}>
                {lookups.priorities.map(row => <button key={row.id} className={`wh-form-option${String(form.priority_id) === String(row.id) ? ' selected' : ''}`} type="button" role="option" onClick={() => { updateField('priority_id', row.id); closeSelect(); }}><span className={`rg-priority ${priorityTone(row.name)}`}>{row.name}</span></button>)}
              </FieldSelect>
              {errors.priority_id && <small className="wh-form-error">{errors.priority_id}</small>}
            </div>
            <label className="rg-field" htmlFor="inv-marko">
              <span>Marko</span>
              <span className={`wh-form-control${errors.marko ? ' invalid' : ''}`}>
                <input aria-invalid={Boolean(errors.marko)} maxLength={100} onBlur={() => touchField('marko')} id="inv-marko" value={form.marko} placeholder="Enter marko" onChange={event => updateField('marko', event.target.value)} />
              </span>
              {errors.marko && <small className="wh-form-error">{errors.marko}</small>}
            </label>
            <label className="rg-field" htmlFor="inv-date">
              <span>Receiving Date <em>*</em></span>
              <span className={`wh-form-control${errors.inventory_date ? ' invalid' : ''}`}>
                <input onBlur={() => touchField('inventory_date')} id="inv-date" type="date" aria-label="Inventory Date" value={form.inventory_date} onChange={event => updateField('inventory_date', event.target.value)} />
              </span>
              {errors.inventory_date && <small className="wh-form-error">{errors.inventory_date}</small>}
            </label>
            <label className="rg-field" htmlFor="inv-ref" hidden={!readOnly} style={readOnly ? undefined : { display: 'none' }}>
              <span>Ref No.</span>
              <span className="wh-form-control">
                <input id="inv-ref" value={isEdit ? form.reference_number : newReference} readOnly />
              </span>
            </label>
          </div>
        </section>

        {form.packaging.map((pack, packIndex) => {
          const type = lookups.packaging_types.find(row => String(row.id) === String(pack.packaging_type_id));
          const copy = packagingCopy(type?.name, packIndex);
          const packLevel = pack.measurement_level || form.measurement_level || 'Carton';
          return <section className={`rg-consignment ${packagingKind(type?.name)}`} key={packIndex} inert={readOnly ? true : undefined}>
            <header className="rg-consignment-head">
              <span className="rg-consignment-index">{packIndex + 1}</span>
              <h3>{copy.title}</h3>
              <span className={`rg-badge ${packagingKind(type?.name)}`}>{copy.badge}</span>
              <div className="rg-consignment-actions">
              <button className="rg-remove" type="button" disabled={form.packaging.length === 1} onClick={() => setForm(current => ({ ...current, packaging: current.packaging.filter((_, index) => index !== packIndex) }))}>
                <Trash2 size={13} />{copy.remove}
              </button>
      {!readOnly && <div className="rg-record-measurement">
<span className="rg-measurement-caption">Measurement:</span>
        <fieldset className="inventory-settings-options" disabled={saving}>
          <legend className="rg-measurement-legend">Inventory measurement level</legend>
          {[['Pallet', Layers], ['Carton', Box], ['Product', Package]].filter(([level]) => level !== 'Pallet' || packagingKind(type?.name) !== 'carton').map(([level, Icon]) =>
            <label key={level} className={`inventory-settings-option${packLevel === level ? ' selected' : ''}`}>
              <input type="radio" name={`record-measurement-level-${packIndex}`} aria-label={`${level} measurement${packIndex ? ` ${packIndex + 1}` : ''}`} checked={packLevel === level}
                onChange={() => updatePackagingMeasurement(packIndex, level)} />
              <Icon size={22} className="inventory-settings-option-icon" aria-hidden="true" />
              <strong>{level}</strong><span>{level} totals</span>
              <span className="inventory-settings-check" aria-hidden="true">{packLevel === level && <Check size={12} />}</span>
            </label>)}
        </fieldset>
      </div>}
              </div>
            </header>

            {packLevel !== 'Pallet' && <label className="rg-master-option">
              <input type="checkbox" aria-label={`Use master measurements ${packIndex + 1}`} checked={Boolean(pack.use_master_measurements)} onChange={event => {
                const checked = event.target.checked;
                setForm(current => ({ ...current, packaging: current.packaging.map((row, index) => index === packIndex ? { ...row, use_master_measurements: checked } : row) }));
                setErrors({});
              }} /> Use master measurements for this consignment
            </label>}
            <div className="rg-pack-row">
              <div className="rg-field">
                <span>Packaging Type</span>
                <FieldSelect ariaLabel={`Packaging Type ${packIndex + 1}`} compact open={openSelect === `type-${packIndex}`} onToggle={() => setOpenSelect(openSelect === `type-${packIndex}` ? null : `type-${packIndex}`)} onClose={closeSelect} value={type?.name || ''} placeholder="Select type" error={errors[`pack_type_${packIndex}`]} onValidate={() => touchField(`pack_type_${packIndex}`)}>
                  {lookups.packaging_types.map(row => <button key={row.id} className={`wh-form-option${String(pack.packaging_type_id) === String(row.id) ? ' selected' : ''}`} type="button" role="option" onClick={() => { updatePackaging(packIndex, 'packaging_type_id', row.id); closeSelect(); }}>{row.name}</button>)}
                </FieldSelect>
                    {errors[`pack_type_${packIndex}`] && <small className="wh-form-error">{errors[`pack_type_${packIndex}`]}</small>}
              </div>
              {packagingKind(type?.name) !== 'carton' && <label className="rg-field">
                <span>{copy.qty}</span>
                <span className={`wh-form-control${errors[`pack_qty_${packIndex}`] ? ' invalid' : ''}`}>
                  <input onBlur={() => touchField(`pack_qty_${packIndex}`)} inputMode="decimal" aria-label={`${copy.qty} ${packIndex + 1}`} placeholder="Enter quantity" value={pack.packaging_quantity} onChange={event => updatePackaging(packIndex, 'packaging_quantity', event.target.value)} />
                </span>
                      {errors[`pack_qty_${packIndex}`] && <small className="wh-form-error">{errors[`pack_qty_${packIndex}`]}</small>}
              </label>}
              {(packLevel === 'Pallet' || pack.use_master_measurements) && [['gross_weight', `${packLevel === 'Pallet' ? 'Pallet' : 'Master'} Gross Wt (kg)`], ['net_weight', `${packLevel === 'Pallet' ? 'Pallet' : 'Master'} Net Wt (kg)`], ['cbm', `${packLevel === 'Pallet' ? 'Pallet' : 'Master'} CBM (m³)`]].map(([field, label]) => <label className="rg-field" key={field}>
              <span>{label} {packLevel === 'Pallet' ? '*' : ''}</span>
              <span className={`wh-form-control${errors[`pack_${field}_${packIndex}`] ? ' invalid' : ''}`}><input aria-label={`${label} ${packIndex + 1}`} inputMode="decimal" value={pack[field] || ''} onBlur={() => touchField(`pack_${field}_${packIndex}`)} onChange={event => updatePackaging(packIndex, field, event.target.value)} /></span>
              {errors[`pack_${field}_${packIndex}`] && <small className="wh-form-error">{errors[`pack_${field}_${packIndex}`]}</small>}
            </label>)}</div>
            {errors[`cartons_${packIndex}`] && <p className="wh-form-error">{errors[`cartons_${packIndex}`]}</p>}
            <div className="rg-batch rg-carton-group">
              <header className="rg-batch-head"><span className="rg-batch-icon" aria-hidden="true"><Box size={13} /></span><strong>Carton</strong></header>
              <div className="rg-carton-scroll">
            {pack.batches.map((batch, batchIndex) => {
              const measurementKey = `${packIndex}-${batchIndex}`;
              const measurementsOpen = packLevel === 'Product' || Boolean(productMeasurements[measurementKey]) || Object.keys(errors).some(key => ['net_weight', 'gross_weight', 'cbm'].some(field => key.startsWith(`product_${field}_${packIndex}_${batchIndex}_`)) && errors[key]);
              const removeCarton = () => {
                if (pack.batches.length > 1) setCartonToDelete({ packIndex, batchIndex });
              };
              const addProduct = () => {
                const productLabel = index => packIndex === 0 && batchIndex === 0 ? index + 1 : `${packIndex + 1}-${batchIndex + 1}-${index + 1}`;
                if (!measurementsOpen) {
                  setProductMeasurements(current => ({ ...current, [measurementKey]: true }));
                }
                setPendingProductFocus(`Product ${productLabel(batch.products.length)}`);
                setForm(current => ({
                ...current,
                packaging: current.packaging.map((row, index) => index === packIndex ? {
                  ...row, batches: row.batches.map((carton, inner) => inner === batchIndex
                    ? { ...carton, products: [...carton.products, emptyProduct(lookups, carton)] } : carton),
                } : row),
              }));
              };
              const cartonField = field => ({
                'batch_quantity': (<label className="rg-field">
                    <span>Carton Qty</span>
                    <span className={`wh-form-control${errors[`batch_qty_${packIndex}_${batchIndex}`] ? ' invalid' : ''}`}>
                      <input onBlur={() => touchField(`batch_qty_${packIndex}_${batchIndex}`)} inputMode="decimal" aria-label={`Carton Qty ${packIndex + 1}-${batchIndex + 1}`} placeholder="Enter quantity" value={batch.batch_quantity} onChange={event => updateBatch(packIndex, batchIndex, 'batch_quantity', event.target.value)} />
                    </span>
                      {errors[`batch_qty_${packIndex}_${batchIndex}`] && <small className="wh-form-error">{errors[`batch_qty_${packIndex}_${batchIndex}`]}</small>}
                  </label>),
                'net_weight': (<label className="rg-field">
                    <span>Carton Net Wt (kg) {packLevel === 'Carton' && !(pack.use_master_measurements && Number(pack.net_weight) > 0) ? '*' : ''}</span>
                    <span className={`wh-form-control${errors[`batch_net_weight_${packIndex}_${batchIndex}`] ? ' invalid' : ''}`}>
                      <input onBlur={() => touchField(`batch_net_weight_${packIndex}_${batchIndex}`)} aria-invalid={Boolean(errors[`batch_net_weight_${packIndex}_${batchIndex}`])} inputMode="decimal" aria-label={`Carton Net ${packIndex + 1}-${batchIndex + 1}`} placeholder="Enter net weight" value={batch.net_weight} onChange={event => updateBatch(packIndex, batchIndex, 'net_weight', event.target.value)} />
                    </span>
                    {errors[`batch_net_weight_${packIndex}_${batchIndex}`] && <small className="wh-form-error">{errors[`batch_net_weight_${packIndex}_${batchIndex}`]}</small>}
                  </label>),
                'gross_weight': (<label className="rg-field">
                    <span>Carton Gross Wt (kg) {packLevel === 'Carton' && !(pack.use_master_measurements && Number(pack.gross_weight) > 0) ? '*' : ''}</span>
                    <span className={`wh-form-control${errors[`batch_gross_weight_${packIndex}_${batchIndex}`] ? ' invalid' : ''}`}>
                      <input onBlur={() => touchField(`batch_gross_weight_${packIndex}_${batchIndex}`)} aria-invalid={Boolean(errors[`batch_gross_weight_${packIndex}_${batchIndex}`])} inputMode="decimal" aria-label={`Carton Gross ${packIndex + 1}-${batchIndex + 1}`} placeholder="Enter gross weight" value={batch.gross_weight} onChange={event => updateBatch(packIndex, batchIndex, 'gross_weight', event.target.value)} />
                    </span>
                    {errors[`batch_gross_weight_${packIndex}_${batchIndex}`] && <small className="wh-form-error">{errors[`batch_gross_weight_${packIndex}_${batchIndex}`]}</small>}
                  </label>),
                'cbm': (<label className="rg-field">
                    <span>Carton CBM (m³) {packLevel === 'Carton' && !(pack.use_master_measurements && Number(pack.cbm) > 0) ? '*' : ''}</span>
                    <span className={`wh-form-control${errors[`batch_cbm_${packIndex}_${batchIndex}`] ? ' invalid' : ''}`}>
                      <input onBlur={() => touchField(`batch_cbm_${packIndex}_${batchIndex}`)} aria-invalid={Boolean(errors[`batch_cbm_${packIndex}_${batchIndex}`])} inputMode="decimal" aria-label={`Carton CBM ${packIndex + 1}-${batchIndex + 1}`} placeholder="Enter CBM" value={batch.cbm} onChange={event => updateBatch(packIndex, batchIndex, 'cbm', event.target.value)} />
                    </span>
                    {errors[`batch_cbm_${packIndex}_${batchIndex}`] && <small className="wh-form-error">{errors[`batch_cbm_${packIndex}_${batchIndex}`]}</small>}
                  </label>)
              })[field];
              return <div className="rg-carton-entry" key={batchIndex}>
                <div className={`rg-products rg-carton-products${measurementsOpen ? ' rg-measurements-open' : ''}`}>
                  <div className="rg-products-head">
                    <span>Carton Qty</span>
                    <span>Product</span>
                    <span>Qty</span>
                    <span>Unit</span>
                    <span hidden={!measurementsOpen} className="rg-product-measurement">Gross Wt (kg) {packLevel === 'Product' && !(pack.use_master_measurements && Number(pack.gross_weight) > 0) ? '*' : ''}</span>
                    <span hidden={!measurementsOpen} className="rg-product-measurement">Net Wt (kg) {packLevel === 'Product' && !(pack.use_master_measurements && Number(pack.net_weight) > 0) ? '*' : ''}</span>
                    <span hidden={!measurementsOpen} className="rg-product-measurement">CBM (m³) {packLevel === 'Product' && !(pack.use_master_measurements && Number(pack.cbm) > 0) ? '*' : ''}</span>
                    <span>Carton Gross Wt (kg) {packLevel === 'Carton' && !(pack.use_master_measurements && Number(pack.gross_weight) > 0) ? '*' : ''}</span>
                    <span>Carton Net Wt (kg) {packLevel === 'Carton' && !(pack.use_master_measurements && Number(pack.net_weight) > 0) ? '*' : ''}</span>
                    <span>Carton CBM (m³) {packLevel === 'Carton' && !(pack.use_master_measurements && Number(pack.cbm) > 0) ? '*' : ''}</span>
                    {!readOnly && <span>Action</span>}
                  </div>
                  {batch.products.map((product, productIndex) => {
                    const productUnit = lookups.units.find(row => String(row.id) === String(product.unit_id || batch.unit_id));
                    const productAria = packIndex === 0 && batchIndex === 0 ? productIndex + 1 : `${packIndex + 1}-${batchIndex + 1}-${productIndex + 1}`;
                    return <div className={`rg-product-row${productIndex > 0 ? ' rg-additional-product-row' : ''}`} key={productIndex}>
                      {productIndex === 0 ? cartonField('batch_quantity') : <span className="rg-carton-placeholder" />}
                      <div className="rg-product-field">
                      <span className={`wh-form-control${errors[`product_${packIndex}_${batchIndex}_${productIndex}`] ? ' invalid' : ''}`}>
                        <input onBlur={() => touchField(`product_${packIndex}_${batchIndex}_${productIndex}`)}
                          aria-label={`Product ${productAria}`}
                          type="text"
                          placeholder="Enter product name"
                          value={product.name}

                          onChange={event => {
                            const name = event.target.value;
                            const match = lookups.products.find(row => row.name === name);
                            setForm(current => ({
                              ...current,
                              packaging: current.packaging.map((row, index) => index === packIndex ? {
                                ...row,
                                batches: row.batches.map((innerBatch, inner) => inner === batchIndex ? {
                                  ...innerBatch,
                                  products: innerBatch.products.map((item, productInner) => (
                                    productInner === productIndex
                                      ? { ...item, name, product_id: match ? match.id : '' }
                                      : item
                                  )),
                                } : innerBatch),
                              } : row),
                            }));
                            setErrors(current => ({
                              ...current,
                              products: undefined,
                              [`products_${packIndex}_${batchIndex}`]: undefined,
                              [`product_${packIndex}_${batchIndex}_${productIndex}`]: undefined,
                            }));
                          }}
                        />
                      </span>
                      {errors[`product_${packIndex}_${batchIndex}_${productIndex}`] && <small className="wh-form-error">{errors[`product_${packIndex}_${batchIndex}_${productIndex}`]}</small>}
                      </div>
                      <div className="rg-product-field rg-product-qty-field">
                      <span className={`wh-form-control${errors[`product_qty_${packIndex}_${batchIndex}_${productIndex}`] ? ' invalid' : ''}`}>
                        <input onBlur={() => touchField(`product_qty_${packIndex}_${batchIndex}_${productIndex}`)} aria-label={`Prod Qty ${productAria}`} inputMode="decimal" value={product.quantity} onChange={event => updateProduct(packIndex, batchIndex, productIndex, 'quantity', event.target.value)} />
                      </span>
                      {errors[`product_qty_${packIndex}_${batchIndex}_${productIndex}`] && <small className="wh-form-error">{errors[`product_qty_${packIndex}_${batchIndex}_${productIndex}`]}</small>}
                      </div>
                      <FieldSelect compact floating floatingMinWidth={0} open={openSelect === `pu-${packIndex}-${batchIndex}-${productIndex}`} onToggle={() => setOpenSelect(openSelect === `pu-${packIndex}-${batchIndex}-${productIndex}` ? null : `pu-${packIndex}-${batchIndex}-${productIndex}`)} onClose={closeSelect} value={productUnit ? (productUnit.code || productUnit.name) : ''} placeholder="Select unit">
                        {lookups.units.map(row => <button key={row.id} className="wh-form-option" type="button" role="option" onClick={() => { updateProduct(packIndex, batchIndex, productIndex, 'unit_id', row.id); closeSelect(); }}>{row.code || row.name}</button>)}
                      </FieldSelect>
                      <span hidden={!measurementsOpen} className="rg-product-field rg-product-measurement"><span className={`wh-form-control${errors[`product_gross_weight_${packIndex}_${batchIndex}_${productIndex}`] ? " invalid" : ""}`}>
                        <input onBlur={() => touchField(`product_gross_weight_${packIndex}_${batchIndex}_${productIndex}`)} aria-label={`Prod Gross ${productAria}`} inputMode="decimal" value={product.gross_weight} onChange={event => updateProduct(packIndex, batchIndex, productIndex, 'gross_weight', event.target.value)} />
                      </span>{errors[`product_gross_weight_${packIndex}_${batchIndex}_${productIndex}`] && <small className="wh-form-error">{errors[`product_gross_weight_${packIndex}_${batchIndex}_${productIndex}`]}</small>}</span>
                      <span hidden={!measurementsOpen} className="rg-product-field rg-product-measurement"><span className={`wh-form-control${errors[`product_net_weight_${packIndex}_${batchIndex}_${productIndex}`] ? " invalid" : ""}`}>
                        <input onBlur={() => touchField(`product_net_weight_${packIndex}_${batchIndex}_${productIndex}`)} aria-label={`Prod Net ${productAria}`} inputMode="decimal" value={product.net_weight} onChange={event => updateProduct(packIndex, batchIndex, productIndex, 'net_weight', event.target.value)} />
                      </span>{errors[`product_net_weight_${packIndex}_${batchIndex}_${productIndex}`] && <small className="wh-form-error">{errors[`product_net_weight_${packIndex}_${batchIndex}_${productIndex}`]}</small>}</span>
                      <span hidden={!measurementsOpen} className="rg-product-field rg-product-measurement"><span className={`wh-form-control${errors[`product_cbm_${packIndex}_${batchIndex}_${productIndex}`] ? " invalid" : ""}`}>
                        <input onBlur={() => touchField(`product_cbm_${packIndex}_${batchIndex}_${productIndex}`)} aria-label={`Prod CBM ${productAria}`} inputMode="decimal" value={product.cbm} onChange={event => updateProduct(packIndex, batchIndex, productIndex, 'cbm', event.target.value)} />
                      </span>{errors[`product_cbm_${packIndex}_${batchIndex}_${productIndex}`] && <small className="wh-form-error">{errors[`product_cbm_${packIndex}_${batchIndex}_${productIndex}`]}</small>}</span>
                      {productIndex === 0 && ['gross_weight', 'net_weight', 'cbm'].map(field => <React.Fragment key={field}>{cartonField(field)}</React.Fragment>)}
                      {!readOnly && <div className="rg-product-actions">
                        {productIndex === 0 && <button className="inv-product-add" type="button" aria-label={`Add product to carton ${packIndex + 1}-${batchIndex + 1}`} onClick={addProduct}><Plus size={16} /></button>}
                      <button className="inv-product-remove" type="button" disabled={productIndex === 0 && pack.batches.length === 1} aria-label={productIndex === 0 ? `Delete carton ${packIndex + 1}-${batchIndex + 1}` : `Remove product ${productAria}`} onClick={() => productIndex === 0 ? removeCarton() : setForm(current => ({
                        ...current,
                        packaging: current.packaging.map((row, index) => index === packIndex ? {
                          ...row,
                          batches: row.batches.map((innerBatch, inner) => inner === batchIndex ? { ...innerBatch, products: innerBatch.products.filter((_, productInner) => productInner !== productIndex) } : innerBatch),
                        } : row),
                      }))}>
                        <Trash2 size={14} />
                      </button>
                      </div>}
                      {['gross_weight', 'net_weight', 'cbm'].map(field => errors[`product_limit_${field}_${packIndex}_${batchIndex}_${productIndex}`] && <small key={field} className={`wh-form-error rg-product-limit-error rg-product-limit-error-${field}`} role="alert">{errors[`product_limit_${field}_${packIndex}_${batchIndex}_${productIndex}`]}</small>)}
                    </div>;
                  })}
                   {errors[`products_${packIndex}_${batchIndex}`] && <small className="wh-form-error">{errors[`products_${packIndex}_${batchIndex}`]}</small>}
                  {!batch.products.length && <div className="rg-product-row rg-empty-carton-row">
                    {cartonField('batch_quantity')}<span /><span /><span />
                    {measurementsOpen && <><span /><span /><span /></>}
                    {['gross_weight', 'net_weight', 'cbm'].map(field => <React.Fragment key={field}>{cartonField(field)}</React.Fragment>)}
                    {!readOnly && <div className="rg-product-actions"><button className="inv-product-add" type="button" aria-label={`Add product to carton ${packIndex + 1}-${batchIndex + 1}`} onClick={addProduct}><Plus size={16} /></button><button className="inv-product-remove" type="button" disabled={pack.batches.length === 1} aria-label={`Delete carton ${packIndex + 1}-${batchIndex + 1}`} onClick={removeCarton}><Trash2 size={14} /></button></div>}
                  </div>}
                </div>
              </div>;
            })}
            {!readOnly && <button className="inv-add-product" type="button" onClick={() => {
              setPendingProductFocus(`Product ${packIndex + 1}-${pack.batches.length + 1}-1`);
              setForm(current => ({
              ...current,
              packaging: current.packaging.map((row, index) => index === packIndex
                ? { ...row, batches: [...row.batches, emptyBatch(lookups)] } : row),
            })); }}><Plus size={14} />Add Carton</button>}
              </div>
            </div>
          </section>;
        })}

        <button className="rg-add-consignment" type="button" onClick={() => { setPendingProductFocus(`Packaging Type ${form.packaging.length + 1}`); setForm(current => ({ ...current, packaging: [...current.packaging, emptyPackaging(lookups)] })); }}>
          <Plus size={14} />Add Another Consignment / Entry
        </button>

        {errors.products && <p className="wh-modal-error" role="alert">{errors.products}</p>}
        {errors.form && <p className="wh-modal-error" role="alert">{errors.form}</p>}
    </div>

    {cartonToDelete && <div className="wh-modal-backdrop" onMouseDown={event => {
      if (event.target === event.currentTarget) setCartonToDelete(null);
    }}>
      <div className="wh-modal wh-modal-confirm" role="alertdialog" aria-modal="true" aria-labelledby="delete-carton-title" aria-describedby="delete-carton-description" onKeyDown={event => {
        if (event.key === 'Escape') { event.stopPropagation(); setCartonToDelete(null); }
      }}>
        <h2 id="delete-carton-title">Delete Carton?</h2>
        <p id="delete-carton-description" className="wh-modal-copy">This carton and all its products will be deleted. Do you want to continue?</p>
        <div className="wh-modal-actions">
          <button className="wh-btn-ghost" type="button" autoFocus onClick={() => setCartonToDelete(null)}>Cancel</button>
          <button className="wh-btn-danger" type="button" onClick={() => {
            setForm(current => ({
              ...current,
              packaging: current.packaging.map((pack, index) => index === cartonToDelete.packIndex && pack.batches.length > 1
                ? { ...pack, batches: pack.batches.filter((_, inner) => inner !== cartonToDelete.batchIndex) } : pack),
            }));
            setCartonToDelete(null);
          }}>Delete Carton</button>
        </div>
      </div>
    </div>}

    {!readOnly && <footer className="rg-footer">
      <button className="wh-form-cancel" type="button" onClick={onClose} disabled={saving}>Cancel</button>
      <button className="wh-form-save" type="button" onClick={handleSubmit} disabled={saving}>
        {saving ? 'Saving...' : <><Check size={15} strokeWidth={2.2} />Save & Receive Goods</>}
      </button>
    </footer>}

    {customerToAdd && createPortal(<div className="wh-page rg-customer-popup" onSubmit={event => event.stopPropagation()}>
      <CustomerModal mode="add" customer={customerToAdd} onClose={() => setCustomerToAdd(null)} onSubmit={saveNewCustomer} />
    </div>, document.body)}
  </form>;
}

export function inventoryPayload(form, lookups, reference = form.reference_number) {
  return {
        measurement_level: form.measurement_level,
        warehouse_id: Number(form.warehouse_id),
        customer_id: Number(form.customer_id),
        priority_id: Number(form.priority_id),
        status: form.status,
        marko: form.marko.trim(),
        reference_number: reference,
        inventory_date: form.inventory_date,
        packaging: form.packaging.map(pack => ({
          measurement_level: pack.measurement_level || form.measurement_level || 'Carton',
          use_master_measurements: pack.use_master_measurements === true,
          cbm: Number(pack.cbm) || 0, net_weight: Number(pack.net_weight) || 0, gross_weight: Number(pack.gross_weight) || 0,
          packaging_type_id: Number(pack.packaging_type_id),
          ...packagingQuantities(pack, lookups),
          batches: pack.batches.map(batch => ({
            priority_id: Number(form.priority_id),
            batch_number: batch.batch_number.trim(),
            batch_quantity: Number(batch.batch_quantity),
            unit_id: Number(batch.unit_id),
            cbm: Number(batch.cbm) || 0,
            net_weight: Number(batch.net_weight) || 0,
            gross_weight: Number(batch.gross_weight) || 0,
            products: batch.products.filter(product => product.name.trim() || product.product_id).map(product => ({
              inventory_batch_product_id: product.inventory_batch_product_id,
              product_id: product.product_id ? Number(product.product_id) : undefined,
              name: product.name.trim() || lookups.products.find(row => String(row.id) === String(product.product_id))?.name || '',
              quantity: Number(product.quantity),
              unit_id: Number(product.unit_id || batch.unit_id),
              cbm: Number(product.cbm) || 0,
              net_weight: Number(product.net_weight) || 0,
              gross_weight: Number(product.gross_weight) || 0,
            })),
          })),
        })),
      };
}
